import { useInView, useMotionValueEvent, useScroll } from 'motion/react';
import { Suspense, lazy, useRef, type RefObject } from 'react';

import { SceneErrorBoundary } from './SceneErrorBoundary';
import { useDeviceTier } from './useDeviceTier';
import type { ScrollRef } from './HeroScene';

/**
 * Lazy, and behind a capability check, so `three` is downloaded only by the
 * devices that will actually draw with it.
 *
 * The order matters more than the split itself: `useDeviceTier` resolves first and
 * returns `none` for no-WebGL and for reduced-motion, and this component returns
 * before touching the import. A phone or a visitor who asked for less motion
 * therefore fetches zero bytes of WebGL — not a deferred half-megabyte, none.
 */
const HeroCanvas = lazy(() => import('./HeroCanvas'));

/**
 * The WebGL hero layer.
 *
 * Strictly decorative: `aria-hidden`, no pointer events, and absent entirely on
 * devices that cannot or should not run it — the CSS hero underneath is a complete
 * design on its own, which is what makes it safe to drop this without a substitute.
 *
 * Scroll progress is handed to the scene through a ref rather than React state. A
 * re-render per scroll frame would fight the render loop it is driving.
 */
export function Scene3D({ target }: { target: RefObject<HTMLElement> }): JSX.Element | null {
  const settings = useDeviceTier();
  const scroll = useRef(0) as ScrollRef;
  // A margin, so the loop is already running by the time the hero scrolls back
  // into view rather than resuming from a frozen frame.
  const inView = useInView(target, { margin: '120px' });

  // 0 → hero fully in view, 1 → hero fully scrolled past, which is exactly the
  // range the camera rig maps its dolly and rotation onto.
  const { scrollYProgress } = useScroll({
    target,
    offset: ['start start', 'end start'],
  });

  useMotionValueEvent(scrollYProgress, 'change', (value) => {
    scroll.current = value;
  });

  if (settings.tier === 'none') return null;

  return (
    <div className="pointer-events-none absolute inset-0" aria-hidden>
      <SceneErrorBoundary fallback={null}>
        <Suspense fallback={null}>
          <HeroCanvas settings={settings} scroll={scroll} inView={inView} />
        </Suspense>
      </SceneErrorBoundary>
    </div>
  );
}
